import { useSelector } from 'react-redux';
import {
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { Moment } from 'moment';
import { momentTimezone } from '../utils/dateUtils';
import { TState, useActions } from '../../redux/store';
import { allIcons, CirlceIcon } from '../utils/Icon';
import { useGetSelect } from '../../redux/actions';

const monthsName = [
  '',
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

type TCalendarToolbarProps = {
  currDay: Moment;
  displayWeekend: boolean;
  displayUpdateRDV: boolean;
  forceDistance: boolean;
  isMap: boolean;
  setIsMap: React.Dispatch<React.SetStateAction<boolean>>;
  setForceDistance: React.Dispatch<React.SetStateAction<boolean>>;
  setDisplayWeekend: React.Dispatch<React.SetStateAction<boolean>>;
  changeDay: (moins?: boolean) => void;
};

const CalendarToolbar = ({
  currDay,
  displayWeekend,
  displayUpdateRDV,
  forceDistance,
  isMap,
  setIsMap,
  setForceDistance,
  setDisplayWeekend,
  changeDay,
  children,
}: TCalendarToolbarProps & { children: any }) => {
  const isPlanningEdit = useSelector((state: TState) => state.isPlanningEdit);
  const theme = useSelector((state: TState) => state.theme);
  const triggerDistance = useSelector((state: TState) => state.triggerDistance);
  const modalType = useSelector((state: TState) => state.modalType);
  const isMobile = useSelector((state: TState) => state.isMobile);
  const isTablet = useSelector((state: TState) => state.isTablet);

  const {
    setIsPlanningEdit,
    setCalendarCurrentDay: setCurrDay,
    setTriggerDistance,
  } = useActions();

  const getInputByName = useGetSelect();
  const planningTypes = getInputByName('planning_type');

  const isModal = /addToPlanning|updatePlanning/i.test(modalType);
  const isPC = !isMobile && !isTablet;

  return (
    <Box
      position='relative'
      bgGradient={`linear(to-r, ${theme.light}, ${theme.dark})`}
      color={theme.main}
      borderRadius='7px'
      p={3}
      zIndex={3}
    >
      <Flex w='100%' alignItems='center' userSelect='none' gap={[1, 1, 1, 2]}>
        <CirlceIcon
          as={allIcons.HiChevronLeft}
          onClick={() => changeDay(true)}
          boxSize={theme.fontSize.headerBig}
        />
        <Text
          textAlign='center'
          fontWeight='bold'
          fontSize={theme.fontSize.big}
          maxW='200px'
          flexGrow={1}
        >
          {/* Semaine {currDay.week()}  */}
          {`${monthsName[currDay.format('M') as any]} ${currDay.format(
            'YYYY',
          )}`}
        </Text>
        <CirlceIcon
          as={allIcons.HiChevronRight}
          onClick={() => changeDay(false)}
          boxSize={theme.fontSize.headerBig}
        />
        <Box flexGrow={1} />
        <Tooltip
          label={displayWeekend ? 'Masquer le weekend' : 'Afficher weekend'}
        >
          <span style={{ height: '34px' }}>
            <CirlceIcon
              boxSize='25px'
              icon={null}
              color={displayWeekend ? theme.none : theme.dark}
              bgGradient={
                displayWeekend
                  ? `linear(to-r, ${theme.normal}, ${theme.dark})`
                  : theme.none
              }
              border={displayWeekend ? `1px solid ${theme.none}` : ''}
              onClick={() => setDisplayWeekend((w) => !w)}
              label='WE'
              px={2}
            />
          </span>
        </Tooltip>
        {isModal && (
          <Tooltip label='Calculer la distance'>
            <span>
              <CirlceIcon
                as={allIcons.HiMiniTruck}
                onClick={() => setTriggerDistance((c: boolean) => !c)}
                boxSize={theme.fontSize.headerBig}
                bgColor={triggerDistance ? theme.dark : ''}
                color={triggerDistance ? theme.none : ''}
                border={triggerDistance ? `1px solid ${theme.none}` : ''}
              />
            </span>
          </Tooltip>
        )}
        <Tooltip
          label={`${isPC ? 'Voir la semaine en cours' : "Voir ajourd'hui"}`}
          placement='right'
        >
          <span>
            <CirlceIcon
              as={allIcons.HiCalendarDays}
              onClick={() =>
                setCurrDay(
                  isPC ? momentTimezone().startOf('isoWeek') : momentTimezone(),
                )
              }
              boxSize={theme.fontSize.headerBig}
            />
          </span>
        </Tooltip>
        <Menu>
          <MenuButton>
            <span>
              <CirlceIcon
                as={allIcons.HiListBullet}
                boxSize={theme.fontSize.headerBig}
              />
            </span>
          </MenuButton>
          <MenuList _hover={{ cursor: 'auto' }}>
            <MenuGroup title='Couleurs des planning'>
              {planningTypes?.options?.map((opt) => (
                <MenuItem _hover={{ cursor: 'auto', bg: 'transparent' }}>
                  <Flex alignItems='center' w='100%'>
                    <Text flexGrow={1}>{opt.label}</Text>
                    <Box
                      mt='2px'
                      h='35px'
                      w='35px'
                      backgroundColor={opt.color}
                      mr={2}
                      borderRadius={3}
                    />
                  </Flex>
                </MenuItem>
              ))}
            </MenuGroup>
          </MenuList>
        </Menu>
        {displayUpdateRDV && (
          <>
            <Tooltip label='Modifier les RDV'>
              <span>
                <CirlceIcon
                  as={
                    allIcons[
                      isPlanningEdit
                        ? 'HiPencilSquare'
                        : 'HiOutlinePencilSquare'
                    ]
                  }
                  onClick={() => {
                    setIsPlanningEdit((c: any) => {
                      c && setForceDistance(false);
                      return !c;
                    });
                  }}
                  bgColor={isPlanningEdit ? theme.dark : ''}
                  color={isPlanningEdit ? theme.none : ''}
                  border={isPlanningEdit ? `1px solid ${theme.none}` : ''}
                />
              </span>
            </Tooltip>
            {/* <Text fontSize={theme.fontSize.small} fontWeight='500'></Text>
              <Switch
                isChecked={isPlanningEdit}
                onChange={(e) => {
                  setIsPlanningEdit(e.target.checked);
                  !e.target.checked && setForceDistance(false);
                }}
              /> */}
            {
              <Tooltip label='Afficher les distances'>
                <span>
                  <CirlceIcon
                    disabled={!isPlanningEdit}
                    as={allIcons[forceDistance ? 'HiTruck' : 'HiOutlineTruck']}
                    onClick={() => {
                      setForceDistance((c: any) => !c);
                    }}
                    bgColor={forceDistance ? theme.dark : ''}
                    color={forceDistance ? theme.none : ''}
                    border={forceDistance ? `1px solid ${theme.none}` : ''}
                  />
                </span>
              </Tooltip>
              // <>
              //   <Text fontSize={theme.fontSize.small} fontWeight='500'>
              //     Afficher les distances
              //   </Text>
              //   <Switch
              //     isChecked={forceDistance}
              //     onChange={(e) => setForceDistance(e.target.checked)}
              //   />
              // </>
            }
          </>
        )}
        {/* <CirlceIcon
          as={allIcons[!isMap ? 'HiMap' : 'HiCalendarDays']}
          onClick={() => setIsMap((m) => !m)}
        /> */}
      </Flex>
      {children}
    </Box>
  );
};

export default CalendarToolbar;
